<script setup>
import { ref, watch } from 'vue';
import { useBIQueryBuilder } from '~/bi/composables/useBIQueryBuilder.js';

defineOptions({ name: 'BiQueryBuilderColumnsHierarchy' });

const props = defineProps({
  node: { type: Object, required: true },
  table: { type: Object, required: true },
  depth: { type: Number, default: 0 },
  isStageColumns: { type: Boolean, default: false },
  hasFunctions: { type: Boolean, default: true },
  hoveredColumn: { type: Object },
  supportedTypes: { type: Array, default: () => [] },
  showOnlyBins: { type: Boolean, default: false },
  autoOpen: { type: Boolean, default: false },
});

const emit = defineEmits(['chevronClick', 'select']);

const { getIconsForType, getOperatorsForType } = useBIQueryBuilder();

const open = ref(props.autoOpen);
watch(() => props.autoOpen, (val) => {
  if (val) {
    open.value = true;
  }
});

function toggle() {
  open.value = !open.value;
}

function onChevronClick(e) {
  emit('chevronClick', { e, column: props.node, table: props.table });
}

function onSelect(e) {
  if (props.node.is_hierarchy) {
    toggle();
    return;
  }
  emit('select', { e, column: props.node, table: props.table });
}

function getColumnDisabledCondition(column) {
  return !(props.supportedTypes?.length === 0 || props.supportedTypes?.includes(column.type));
}
</script>

<template>
  <div>
    <div
      v-if="node.is_hierarchy"
      class="flex items-center justify-between gap-2"
      :class="`pl-${2 + depth} py-2 cursor-pointer group hover:bg-gray-50 rounded-lg relative`"
      @click.stop="toggle"
    >
      <div class="flex items-center">
        <IconHawkChevronRight :class="open ? 'rotate-90' : ''" class="text-gray-600 size-4  min-w-4  mr-2 transition-transform" />
        <span class="text-gray-700 text-sm font-medium">{{ node.label }}</span>
      </div>
    </div>

    <div
      v-else
      class="flex items-center justify-between gap-2 pl-3 py-2 cursor-pointer group hover:bg-gray-50 rounded-lg relative"
      :class="{ 'bg-gray-50': hoveredColumn?.field === node.field, 'pl-3': isStageColumns, 'pl-6': !isStageColumns, 'pointer-events-none opacity-50': getColumnDisabledCondition(node) }"
      @click="onSelect($event)"
    >
      <div class="flex items-center">
        <component :is="getIconsForType(node.type, node.resource_type)" class="text-gray-600 size-4 min-w-4 mr-2" />
        <span class="text-gray-700 text-sm font-medium">{{ node.label }}</span>
      </div>
      <hawk-button v-if="(getOperatorsForType(node.type).length && hasFunctions) || (showOnlyBins && ['date', 'timestamp'].includes(node.type))" icon size="xxs" type="light" color="gray" class="text-gray-500 size-4 min-w-4 mr-2 -mt-1  group-hover:visible" :class="hoveredColumn?.field === node.field ? 'visible' : 'invisible'" @click.stop="onChevronClick($event)">
        <IconHawkChevronRight />
      </hawk-button>
    </div>

    <div v-if="node.is_hierarchy && open" class="pl-2">
      <BiQueryBuilderColumnsHierarchy
        v-for="child in node.children"
        :key="child.label"
        :node="child"
        :table="table"
        :depth="depth + 1"
        :has-functions="hasFunctions"
        :hovered-column="hoveredColumn"
        :supported-types="supportedTypes"
        :show-only-bins="showOnlyBins"
        :auto-open="autoOpen"
        @chevron-click="$emit('chevronClick', $event)"
        @select="$emit('select', $event)"
      />
    </div>
  </div>
</template>
